import { isValid, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import type { IDriverAppDownloadMessageAction } from '../../types/tracking'
import { AuthorizationStatusEnum, DriverAppDownloadMessageStatusEnum } from '../../types/tracking'
import { dateTimeAgo } from '../../utils/datetime-utils'
import { formatPhoneNumber } from '../../utils/phone'

/**
 * Returns true if the mobile app is tracking for the mobile app tracking source
 */
export const isDeviceTracking = (params: {
  authorizationStatus: AuthorizationStatusEnum | undefined
  hasSentMockLocations: boolean
  isHistoricalDevice: boolean
  isShipmentOwner?: boolean
  lastLocationTimestamp: string | null
}) => {
  const { authorizationStatus, hasSentMockLocations, isHistoricalDevice, lastLocationTimestamp, isShipmentOwner } =
    params

  if (hasSentMockLocations) {
    // Only show fake location reason to shipment owners
    if (isShipmentOwner) {
      return { isTracking: false, reason: 'The driver is sending fake locations' }
    } else {
      return { isTracking: false, reason: 'Device is not tracking' }
    }
  }

  if (isHistoricalDevice) return { isTracking: false, reason: 'The device is not tracking yet' }
  if (authorizationStatus !== undefined && authorizationStatus !== null) {
    if (authorizationStatus === AuthorizationStatusEnum.AuthorizationStatusAlways) return { isTracking: true }
    return { isTracking: false, reason: `Cannot track with "${authorizationStatus}" permission` }
  }
  if (lastLocationTimestamp) return { isTracking: true }
  return { isTracking: false }
}

/**
 * Returns a formatted string combining absolute local time and relative time
 * for the provided driver-app download SMS action. Example:
 *   "3/25 1:10 PM (3 hours ago)"
 *
 * If the relevant timestamp is missing the function returns `null`.
 */
export const formatDriverAppActionTime = (
  action: IDriverAppDownloadMessageAction | null | undefined
): string | null => {
  if (!action) return null

  const { status, completedAt, canceledAt, messageDueDate } = action

  const toLocalAbsolute = (utcDateString: string): string | null => {
    // Return null if the date string is empty or invalid
    if (!utcDateString || typeof utcDateString !== 'string') {
      return null
    }

    try {
      const parsedDate = parseISO(utcDateString)
      if (!isValid(parsedDate)) {
        return null
      }
      const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone
      return formatInTimeZone(parsedDate, userTz, 'M/d h:mm a')
    } catch (error) {
      // Handle any date formatting errors gracefully
      console.error('Error formatting date:', error, 'Date string:', utcDateString)
      return null
    }
  }

  if (status === DriverAppDownloadMessageStatusEnum.Completed && completedAt) {
    const absoluteTime = toLocalAbsolute(completedAt)
    if (!absoluteTime) return null
    return `${absoluteTime} (${dateTimeAgo(completedAt)})`
  }
  if (status === DriverAppDownloadMessageStatusEnum.Canceled && canceledAt) {
    const absoluteTime = toLocalAbsolute(canceledAt)
    if (!absoluteTime) return null
    return `${absoluteTime} (${dateTimeAgo(canceledAt)})`
  }
  if (status === DriverAppDownloadMessageStatusEnum.Scheduled && messageDueDate) {
    const absoluteTime = toLocalAbsolute(messageDueDate)
    if (!absoluteTime) return null
    return `${absoluteTime} (${dateTimeAgo(messageDueDate)})`
  }

  return null
}

/**
 * Builds the human-readable status message shown in the historical-device row.
 * Examples:
 *   "Tracking request SMS scheduled to send 3/25 1:10 PM (3 hours ago) to +1 555-123-4567"
 *   "Tracking request SMS pending for the driver"
 */
export const buildDriverAppRequestStatus = (
  latestAction: IDriverAppDownloadMessageAction | null,
  userPhone: string | null
): string => {
  const phoneLabel = userPhone ? formatPhoneNumber(userPhone) : 'the driver'

  if (!latestAction) {
    return `Tracking request SMS pending for ${phoneLabel}`
  }

  const actionTime = formatDriverAppActionTime(latestAction)

  const statusDescription =
    latestAction.status === DriverAppDownloadMessageStatusEnum.Scheduled
      ? 'scheduled to send'
      : latestAction.status === DriverAppDownloadMessageStatusEnum.Canceled
        ? 'cancelled'
        : 'sent'

  return `Tracking request SMS ${statusDescription}${actionTime ? ` ${actionTime}` : ''} to ${phoneLabel}`
}
